// ผลสอบรายคน + ส่งออก Excel (มีคำตอบรายข้อและถูก/ผิดครบ ให้ครูเอาไปทำต่อเองได้)
import React, { useMemo, useState } from 'react'
import { Badge, Button, Form, Table } from 'react-bootstrap'
import * as XLSX from 'xlsx'
import { answerLabel, fmt, isObjective, keyLabel, roomLabel } from './examUtils'

const inactive = (s) => String(s.st_status) === '0' || s.transfer_room_id != null

export default function ExamResults({ exam, questions, students, showRoom, totalPoints }) {
  const [sort, setSort] = useState('seat')
  const written = questions.filter((q) => q.eq_type === 'written')

  const rows = useMemo(() => {
    const list = students.filter((s) => s.score || !inactive(s))
    if (sort === 'score') {
      return [...list].sort((a, b) => (b.score?.total ?? -1) - (a.score?.total ?? -1))
    }
    return list
  }, [students, sort])

  const exportExcel = () => {
    const header = [
      ...(showRoom ? ['ห้อง'] : []), 'เลขที่', 'เลขประจำตัว', 'ชื่อ-สกุล',
      ...questions.map((q) => `ข้อ ${q.eq_no}`),
      'ปรนัย', 'รวม', `ร้อยละ (เต็ม ${fmt(totalPoints)})`, 'สถานะ',
    ]
    const keyRow = [
      ...(showRoom ? [''] : []), '', '', 'เฉลย',
      ...questions.map((q) => (q.eq_type === 'written' ? `อัตนัย ${fmt(q.eq_points)}` : keyLabel(q.eq_key))),
      '', '', '', '',
    ]
    const body = rows.map((s) => [
      ...(showRoom ? [roomLabel(s)] : []), s.seat, s.st_idnumber, s.st_name,
      ...questions.map((q) => {
        const a = s.answers?.[q.eq_no]
        if (!s.score) return ''
        if (q.eq_type === 'written') return a?.ea_score ?? ''
        const got = s.score.perQuestion[q.eq_no]
        const label = answerLabel(a?.ea_answer) || '-'
        return got === null ? label : `${label}${got > 0 ? ' ✓' : ' ✗'}`
      }),
      s.score ? s.score.objective : '',
      s.score ? s.score.total : '',
      s.score && totalPoints ? Math.round((s.score.total / totalPoints) * 10000) / 100 : '',
      !s.score ? 'ไม่มีกระดาษคำตอบ' : s.score.complete ? '' : 'ยังตรวจไม่ครบ',
    ])
    const ws = XLSX.utils.aoa_to_sheet([header, keyRow, ...body])
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'ผลสอบ')
    XLSX.writeFile(wb, `${exam.exam_name}${showRoom ? '' : ` ${roomLabel(students[0] || {})}`}.xlsx`.replace(/[\\/:*?"<>|]/g, '-'))
  }

  const answered = rows.filter((s) => s.score)

  return (
    <>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <div className="fs-sm text-secondary me-auto">
          มีกระดาษคำตอบ {answered.length} จาก {rows.length} คน
        </div>
        <Form.Select size="sm" style={{ width: 170 }} value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="seat">เรียงตามเลขที่</option>
          <option value="score">เรียงตามคะแนน</option>
        </Form.Select>
        <Button size="sm" variant="outline-success" disabled={!answered.length} onClick={exportExcel}>
          <i className="ri-file-excel-2-line"></i> ส่งออก Excel
        </Button>
      </div>
      <div className="table-responsive">
        <Table size="sm" hover className="align-middle mb-0">
          <thead>
            <tr>
              {showRoom && <th>ห้อง</th>}
              <th style={{ width: 45 }}>เลขที่</th>
              <th>ชื่อ-สกุล</th>
              {questions.some(isObjective) && <th className="text-end">ปรนัย</th>}
              {written.length > 0 && <th className="text-end">อัตนัย</th>}
              <th className="text-end">รวม</th>
              <th className="text-end">ร้อยละ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.study_id}>
                {showRoom && <td className="text-nowrap">{roomLabel(s)}</td>}
                <td>{s.seat}</td>
                <td>
                  <span className="text-nowrap">{s.st_name}</span>
                  {inactive(s) && <Badge bg="secondary" className="ms-1">{String(s.st_status) === '0' ? 'ออกแล้ว' : 'ย้ายห้อง'}</Badge>}
                </td>
                {!s.score ? (
                  <td colSpan={(questions.some(isObjective) ? 1 : 0) + (written.length ? 1 : 0) + 2} className="text-secondary text-end">
                    ไม่มีกระดาษคำตอบ
                  </td>
                ) : (
                  <>
                    {questions.some(isObjective) && <td className="text-end">{fmt(s.score.objective)}</td>}
                    {written.length > 0 && (
                      <td className="text-end">
                        {s.score.pending.length
                          ? <span className="text-warning">รอตรวจ {s.score.pending.length} ข้อ</span>
                          : fmt(s.score.total - s.score.objective)}
                      </td>
                    )}
                    <td className="text-end fw-semibold">{fmt(s.score.total)}</td>
                    <td className="text-end">{totalPoints ? fmt((s.score.total / totalPoints) * 100, 1) : '-'}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </>
  )
}
